import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/native';

import { ELoginAs } from '@webtrader/core/constants/account';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { useTranslation } from '@webtrader/core/i18n';
import { authService } from '@webtrader/core/services/auth';
import { companyService } from '@webtrader/core/services/company';
import { demoCreationService, useDemoCreation } from '@webtrader/core/services/demoCreation';
import { useTheme } from '@webtrader/core/services/theme/v2';
import { checkIsNative } from '@webtrader/core/validators/device';

import Button from '@/components/Button';
import IonicIcons from '@/components/Icons/Ionic';
import Text from '@/components/Text';
import { ALL_MOBILE_ROUTES, goBack, replace, useDemoCompletionRouteParams } from '@/utils/navigation';

import DemoCompletionContent from './Content';

interface IProps {}

const DemoAccountCompletion: FC<IProps> = () => {
  const params = useDemoCompletionRouteParams();
  const [initialParams] = useState(params);

  const { publicAuth, setNeedUpdateFields } = initialParams || {};

  const { t } = useTranslation();
  const { theme } = useTheme();
  const { demoAccountInfo } = useDemoCreation();

  const onGoToLoginPage = useCallback(() => {
    authService
      .logout({
        loginAsDemo: false
      })
      .then(() => {
        if (!checkIsNative()) {
          replace(ALL_MOBILE_ROUTES.AUTH.LOGIN);
        }
      });
  }, []);

  const onDone = useCallback(() => {
    goBack();
  }, []);

  const onSwitchToDemo = useCallback(() => {
    authService.setPrefilledValueDemoAccount(demoAccountInfo?.login, demoAccountInfo?.password);

    authService
      .logout({
        loginAsDemo: true,
        onRedirect: undefined
      })
      .then(() => {
        if (!checkIsNative()) {
          goBack();
        }
      });
  }, [demoAccountInfo]);

  const onSignIn = useCallback(() => {
    setNeedUpdateFields?.(true);

    if (publicAuth) {
      const { mt5DemoLoginConfig, mt4DemoLoginConfig } = companyService.context.access;

      const demoLoginType = authService.getDemoLoginTypeFromCompanyAccess({ mt5DemoLoginConfig, mt4DemoLoginConfig });

      authService.setLoginAs(demoLoginType as ELoginAs);
      authService.setInitialContext(false);
      authService.setPrefilledValueDemoAccount(demoAccountInfo?.login, demoAccountInfo?.password);

      goBack();
    } else {
      onSwitchToDemo();
    }
  }, [publicAuth, demoAccountInfo?.login, demoAccountInfo?.password, setNeedUpdateFields, onSwitchToDemo]);

  const buttons = useMemo(() => {
    if (!demoAccountInfo || demoAccountInfo === undefined) {
      return (
        <SuccessActions>
          <SuccessActionItem>
            <Button variant="subtle" onPress={() => onGoToLoginPage()} testID={EDataTestId.DEMO_COMPLETION_GO_TO_LOGIN}>
              {t('GO TO LOGIN PAGE')}
            </Button>
          </SuccessActionItem>
        </SuccessActions>
      );
    }

    if (publicAuth) {
      return (
        <SuccessActions>
          <SuccessActionItem>
            <Button variant="subtle" onPress={() => onSignIn()} testID={EDataTestId.DEMO_COMPLETION_SIGN_IN}>
              {t('SIGN IN')}
            </Button>
          </SuccessActionItem>
        </SuccessActions>
      );
    }

    return (
      <SuccessActions>
        <SuccessActionItem>
          <Button variant="subtle" onPress={() => onSwitchToDemo()} testID={EDataTestId.DEMO_COMPLETION_SWITCH_DEMO}>
            {t('SWITCH TO DEMO ACCOUNT')}
          </Button>
        </SuccessActionItem>
        <SuccessActionItem>
          <Button variant="subtle" onPress={() => onDone()} testID={EDataTestId.DEMO_COMPLETION_DONE}>
            {t('DONE')}
          </Button>
        </SuccessActionItem>
      </SuccessActions>
    );
  }, [demoAccountInfo, publicAuth, t, onSignIn, onSwitchToDemo, onDone, onGoToLoginPage]);

  useEffect(() => {
    return () => {
      demoCreationService.reset();
    };
  }, []);

  return (
    <Screen>
      <CompletionHeader>
        <IonicIcons name="checkmark-circle-outline" size={60} color={theme.colors.success[0]} />
        <CompletionHeaderTitle testID={EDataTestId.DEMO_ACCOUNT_COMPLETION_MODAL_TITLE}>
          {t('Your demo account has been opened successfully.')}
        </CompletionHeaderTitle>
        <CompletionHeaderDescription>{t(`We've also sent these to your email.`)}</CompletionHeaderDescription>
      </CompletionHeader>
      <DemoCompletionContent />
      {buttons}
    </Screen>
  );
};

export default DemoAccountCompletion;

const Screen = styled.View`
  flex: 1;

  background-color: ${({ theme }) => theme.colors.background[1]};
`;

const CompletionHeader = styled.View`
  display: flex;
  padding: ${dynamicSize.convertSize(24)}px;

  justify-content: center;
  align-items: center;

  background-color: ${({ theme }) => theme.colors.background[0]};
`;

const CompletionHeaderTitle = styled(Text)`
  font-size: ${dynamicSize.convertSize(18)}px;
  font-weight: 700;
  text-align: center;

  margin-top: ${dynamicSize.convertSize(32)}px;
  margin-bottom: ${dynamicSize.convertSize(16)}px;

  color: ${({ theme }) => theme.colors.text[0]};
`;

const CompletionHeaderDescription = styled(Text)`
  font-size: 14px;
  font-weight: 400;

  color: ${({ theme }) => theme.colors.text[0]};
`;

const SuccessActions = styled.View``;

const SuccessActionItem = styled.View`
  margin-top: ${dynamicSize.convertSize(6)}px;
  background-color: ${({ theme }) => theme.colors.background[0]};
`;
